/** What the unit itself last reported (heartbeat), plus owner actions: rename, new token, remove. */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';
import { api, ApiError } from '../api.ts';

export interface Health {
  online: boolean;
  lastSeenAt: string | null;
  heartbeatAt: string | null;
  firmwareVersion: string | null;
  rssi: number | null;
  uptimeS: number | null;
  freeHeap: number | null;
  activeSet: number | null;
  errors: string[];
  configVersion: number;
  appliedConfigVersion: number | null;
  sync: 'synced' | 'pending' | 'unknown';
  tokenPrefix: string;
  rotationPending: boolean;
}

function duration(s: number) {
  const d = Math.floor(s / 86_400);
  const h = Math.floor((s % 86_400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  return d ? `${d}d ${h}h` : h ? `${h}h ${m}m` : `${m}m`;
}

/** The token is shown once: copy it into the firmware config before leaving the page. */
export function TokenReveal({ token, serial, onDone }: { token: string; serial: string; onDone: () => void }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <div className="card stack" role="status">
      <p className="ok">{t('deviceAdmin.tokenOnce', { serial })}</p>
      <code className="copy">{token}</code>
      <div className="row">
        <button type="button" className="btn btn--small" onClick={() => void navigator.clipboard?.writeText(token).then(() => setCopied(true))}>{copied ? t('deviceAdmin.copied') : t('deviceAdmin.copy')}</button>
        <button type="button" className="btn btn--small btn--ghost" onClick={onDone}>{t('deviceAdmin.done')}</button>
      </div>
      <p className="faint">{t('deviceAdmin.tokenHint')}</p>
    </div>
  );
}

export function DeviceHealth({ health, setNames }: { health: Health | undefined; setNames: Record<number, string> }) {
  const { t } = useTranslation();
  if (!health) return null;
  const status = health.online ? 'online' : health.lastSeenAt ? 'offline' : 'never_connected';
  const rows: [string, string][] = [
    [t('health.lastSeen'), health.lastSeenAt ? new Date(health.lastSeenAt).toLocaleString() : t('health.never')],
    [t('health.activeSet'), health.activeSet === null ? '–' : `${setNames[health.activeSet] ?? t('health.unknownSet')} (${t('configurator.position', { n: health.activeSet + 1 })})`],
    [t('health.firmware'), health.firmwareVersion ?? '–'],
    [t('health.signal'), health.rssi === null ? '–' : `${health.rssi} dBm`],
    [t('health.uptime'), health.uptimeS === null ? '–' : duration(health.uptimeS)],
    [t('health.freeHeap'), health.freeHeap === null ? '–' : `${Math.round(health.freeHeap / 1024)} KB`],
    [t('health.token'), `${health.tokenPrefix}…${health.rotationPending ? ` · ${t('health.rotationPending')}` : ''}`],
  ];
  return (
    <section className="card stack" aria-labelledby="health-title">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h2 id="health-title">{t('health.title')}</h2>
        <span className={`badge badge--${status === 'never_connected' ? 'offline' : status}`}>{t(`devices.status.${status}`)}</span>
      </div>
      <p className={health.sync === 'pending' ? 'warn' : health.sync === 'synced' ? 'ok' : 'faint'}>
        {t(`health.sync.${health.sync}`, { applied: health.appliedConfigVersion, current: health.configVersion })}
      </p>
      <dl className="kv">
        {rows.map(([k, v]) => (
          <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
        ))}
      </dl>
      {health.errors.length > 0 && (
        <div className="stack" style={{ gap: '0.3rem' }}>
          <p className="error">{t('health.errors', { count: health.errors.length })}</p>
          <ul className="error-list">{health.errors.map((e, i) => <li key={i}><code>{e}</code></li>)}</ul>
        </div>
      )}
    </section>
  );
}

export function DeviceAdmin({ id, name, serial }: { id: string; name: string; serial: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [, navigate] = useLocation();
  const [newName, setNewName] = useState(name);
  const [token, setToken] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedName, setSavedName] = useState(false);

  async function run(fn: () => Promise<void>) {
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof ApiError ? `${t('common.error')} (${e.code})` : t('common.error'));
    }
  }

  return (
    <section className="card stack" aria-labelledby="admin-title">
      <h2 id="admin-title">{t('deviceAdmin.title')}</h2>
      <form className="row" style={{ alignItems: 'end' }} onSubmit={(e) => (e.preventDefault(), void run(async () => {
        await api(`/api/devices/${id}`, { method: 'PATCH', body: { name: newName } });
        await qc.invalidateQueries({ queryKey: ['device', id] });
        await qc.invalidateQueries({ queryKey: ['devices'] });
        setSavedName(true);
      }))}>
        <div className="field" style={{ flex: 1, minWidth: '12rem' }}>
          <label htmlFor="dev-name">{t('deviceAdmin.name')}</label>
          <input id="dev-name" value={newName} maxLength={60} required onChange={(e) => (setNewName(e.target.value), setSavedName(false))} />
        </div>
        <button className="btn btn--small btn--ghost" disabled={newName.trim() === name}>{t('deviceAdmin.rename')}</button>
        {savedName && <span className="ok">{t('editor.saved')}</span>}
      </form>

      {token ? (
        <TokenReveal token={token} serial={serial} onDone={() => setToken(null)} />
      ) : (
        <div className="stack" style={{ gap: '0.4rem' }}>
          <p className="faint">{t('deviceAdmin.rotateHint')}</p>
          <div className="row">
            {confirmRotate ? (
              <>
                <button type="button" className="btn btn--small" onClick={() => void run(async () => {
                  setToken((await api<{ token: string }>(`/api/devices/${id}/token`, { method: 'POST' })).token);
                  setConfirmRotate(false);
                  await qc.invalidateQueries({ queryKey: ['live', id] });
                })}>{t('deviceAdmin.rotateConfirm')}</button>
                <button type="button" className="link-btn" onClick={() => setConfirmRotate(false)}>{t('connections.cancel')}</button>
              </>
            ) : (
              <button type="button" className="btn btn--small btn--ghost" onClick={() => setConfirmRotate(true)}>{t('deviceAdmin.rotate')}</button>
            )}
          </div>
        </div>
      )}

      <div className="row">
        {confirmDelete ? (
          <>
            <span className="error">{t('deviceAdmin.deleteWarn', { serial })}</span>
            <button type="button" className="btn btn--small" onClick={() => void run(async () => {
              await api(`/api/devices/${id}`, { method: 'DELETE' });
              await qc.invalidateQueries({ queryKey: ['devices'] });
              navigate('/');
            })}>{t('deviceAdmin.deleteConfirm')}</button>
            <button type="button" className="link-btn" onClick={() => setConfirmDelete(false)}>{t('connections.cancel')}</button>
          </>
        ) : (
          <button type="button" className="link-btn" onClick={() => setConfirmDelete(true)}>{t('deviceAdmin.delete')}</button>
        )}
      </div>
      {error && <p className="error" role="alert">{error}</p>}
    </section>
  );
}
